import { IntlShape } from 'react-intl'
import { getChallengeTypeName } from 'utils/challenges'
import { Link, useLocation } from 'react-router-dom'
import makeStyles from '@mui/styles/makeStyles'
import { Box, Chip, Theme, Tooltip } from '@mui/material'
import { useCallback } from 'react'
import UserChallengeStatusBadge from 'components/challenge/UserChallengeStatusBadge'
import { ClientRoute } from 'routes/client'
import { GridColDef } from '@mui/x-data-grid'
import { getParticipantStatusChip } from 'utils/participantStatusToChip'
import ParticipantRoundsCell from 'components/userChallenge/ParticipantRoundsCell'
import { UserChallenge } from 'api/types'
import StatusBadge from 'components/challenge/StatusBadge'

const useStyles = makeStyles((theme: Theme) => ({
  link: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
  },
}))

interface GetChallengeColumnsParams {
  intl: IntlShape
  selectedChallengeType?: string
  // Opens the rounds the participant has played during the challenge
  onShowRounds?: (challengeId: string, participantId: string) => void
}

export const useGetChallengeColumns = ({ intl, selectedChallengeType, onShowRounds }: GetChallengeColumnsParams) => {
  const classes = useStyles()
  const location = useLocation()

  const renderCreatorLink = (creator: UserChallenge) => {
    if (!creator) {
      return '-'
    }

    return (
      <Link
        className={classes.link}
        to={`${ClientRoute.ADMIN_USER(Number(creator.createdBy?.userId), 'challenges')}`}
        state={{ selectedChallengeType: 'userCreated' }}
      >
        {creator.createdBy?.firstName} {creator.createdBy?.lastName}
      </Link>
    )
  }

  const sponsoredChallengeColumns = [
    {
      field: 'date',
      flex: 1,
      headerName: intl.formatMessage({ id: 'strings.date' }),
    },
    {
      field: 'name',
      flex: 1.5,
      headerName: intl.formatMessage({ id: 'strings.challengeName' }),
      renderCell: (params: any) => (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
            gap: 0.5,
            width: '100%',
            minHeight: '100%',
            py: 0.5,
            lineHeight: 1.2,
          }}
        >
          <Link
            className={classes.link}
            to={ClientRoute.ADMIN_CHALLENGE(params.value.id)}
            style={{
              display: 'block',
              whiteSpace: 'normal',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
              width: '100%',
              textAlign: 'left',
            }}
          >
            {params.value.name}
          </Link>
        </Box>
      ),
    },
    {
      field: 'status',
      flex: 1,
      headerName: intl.formatMessage({ id: 'strings.status' }),
      renderCell: (params: any) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            width: '100%',
            height: '100%',
          }}
        >
          <StatusBadge challengeState={params.value.computedState} />
        </Box>
      ),
    },
    {
      field: 'joined',
      flex: 1,
      headerName: intl.formatMessage({ id: 'strings.joined' }),
      renderCell: (params) => (
        <Tooltip title={'(+00:00 UTC)'}>
          <span>{params.row.joined}</span>
        </Tooltip>
      ),
    },
    {
      field: 'score',
      flex: 1,
      headerName: intl.formatMessage({ id: 'strings.score' }),
    },
    {
      field: 'rounds',
      flex: 1,
      headerName: intl.formatMessage({ id: 'strings.rounds' }),
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
          }}
        >
          <ParticipantRoundsCell
            participantId={params.value?.participantId ?? null}
            onShowRounds={(participantId) => onShowRounds?.(params.value.challengeId, participantId)}
          />
        </Box>
      ),
    },
  ]

  const userCreatedChallengeColumns = [
    {
      field: 'date',
      flex: 1,
      headerName: intl.formatMessage({ id: 'strings.date' }),
    },
    {
      field: 'name',
      flex: 1,
      headerName: intl.formatMessage({ id: 'strings.name' }),
      renderCell: (params: any) => {
        const name = params.value.name
        const displayName = name.length > 35 ? `${name.slice(0, 35)}…` : name

        return (
          <>
            <Link
              className={classes.link}
              to={ClientRoute.ADMIN_USER_CHALLENGE(params.value.id)}
              state={{ from: location.pathname, type: selectedChallengeType }}
              title={name}
            >
              {displayName}
            </Link>
          </>
        )
      },
    },
    {
      field: 'status',
      flex: 1,
      headerName: intl.formatMessage({ id: 'strings.status' }),
      renderCell: (params: any) => (
        <>
          <UserChallengeStatusBadge challengeComputedState={params.value.computedState} />
        </>
      ),
    },
    {
      field: 'type',
      flex: 1,
      headerName: intl.formatMessage({ id: 'challenges.type' }),
      renderCell: (params) => getChallengeTypeName(params.value, intl),
    },
    {
      field: 'participantCount',
      flex: 1,
      headerName: intl.formatMessage({ id: 'challenges.participants' }),
    },
  ]

  const userJoinedChallengeColumns = [
    {
      field: 'date',
      flex: 1,
      headerName: intl.formatMessage({ id: 'strings.date' }),
    },
    {
      field: 'name',
      flex: 1,
      headerName: intl.formatMessage({ id: 'strings.name' }),
      renderCell: (params: any) => {
        const name = params.value.name
        const displayName = name.length > 35 ? `${name.slice(0, 35)}…` : name

        return (
          <>
            <Link
              className={classes.link}
              to={ClientRoute.ADMIN_USER_CHALLENGE(params.value.id)}
              state={{ from: location.pathname, type: selectedChallengeType }}
              title={name}
            >
              {displayName}
            </Link>
          </>
        )
      },
    },
    {
      field: 'status',
      flex: 1,
      headerName: intl.formatMessage({ id: 'strings.status' }),
      renderCell: (params: any) => (
        <>
          <UserChallengeStatusBadge challengeComputedState={params.value.computedState} />
        </>
      ),
    },
    {
      field: 'type',
      flex: 1,
      headerName: intl.formatMessage({ id: 'challenges.type' }),
      renderCell: (params) => getChallengeTypeName(params.value, intl),
    },
    {
      field: 'participantStatus',
      flex: 1,
      headerName: intl.formatMessage({ id: 'challenges.participantStatus' }),
      renderCell: (params) => {
        if (!params.row.participantStatus) {
          return '-'
        }

        const chip = getParticipantStatusChip(params.row.participantStatus)

        return <Chip label={chip.label} size="small" sx={chip.sx} icon={chip.icon} />
      },
    },
    {
      field: 'creator',
      flex: 1,
      headerName: intl.formatMessage({ id: 'strings.creator' }),
      renderCell: (params) => (params.value ? renderCreatorLink(params.value?.creator) : '-'),
    },
    {
      field: 'joined',
      flex: 1,
      headerName: intl.formatMessage({ id: 'strings.joinedAt' }),
    },
    {
      field: 'score',
      flex: 1,
      headerName: intl.formatMessage({ id: 'strings.score' }),
    },
  ]

  const getColumnsByChallengeType = useCallback(
    (challengeType: string) => {
      switch (challengeType) {
        case 'sponsored':
          return sponsoredChallengeColumns as GridColDef[]
        case 'userCreated':
          return userCreatedChallengeColumns as GridColDef[]
        case 'userJoined':
          return userJoinedChallengeColumns as GridColDef[]
        default:
          return []
      }
    },
    [selectedChallengeType, sponsoredChallengeColumns, userCreatedChallengeColumns, userJoinedChallengeColumns],
  )
  return {
    getColumnsByChallengeType,
  }
}
